import React from 'react';

interface QueryResult {
  columns: string[];
  rows: any[][];
  rowCount: number;
}

interface ResultPreviewProps {
  result: QueryResult | null;
  isLoading: boolean;
  error: string | null;
  isPreview: boolean;
}

const ResultPreview: React.FC<ResultPreviewProps> = ({ 
  result, 
  isLoading, 
  error, 
  isPreview 
}) => {
  if (isLoading) {
    return (
      <div className="w-full max-w-6xl mx-auto">
        <div className="neon-panel p-12 text-center">
          <div className="neon-loading mb-6 text-xl">
            [PROCESSING QUERY...]
          </div>
          <div className="font-mono text-lg space-y-3">
            <div className="animate-pulse text-neon-cyan">
              ████████████████████████
            </div>
            <div className="animate-pulse text-neon-purple">
              ████████████████
            </div>
            <div className="animate-pulse text-neon-pink">
              ████████████████████
            </div>
          </div>
          <div className="mt-8 text-neon-blue font-mono text-sm">
            Please wait while DuckDB processes your query...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-6xl mx-auto">
        <div className="neon-error p-8 text-center">
          <div className="neon-panel-header mb-6">
            [ERROR] Query Failed
          </div>
          <div className="space-y-4">
            <div className="font-mono text-lg">
              <strong>SQL Error:</strong>
            </div>
            <div className="font-mono text-sm bg-black bg-opacity-30 p-4 rounded border border-red-600">
              {error}
            </div>
            <div className="font-mono text-xs text-gray-300 space-y-1">
              <div>• Check your SQL syntax</div>
              <div>• Verify column names exist in the table</div>
              <div>• Ensure table name is correct</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="w-full max-w-6xl mx-auto">
        <div className="neon-panel p-12 text-center">
          <div className="space-y-6">
            <div className="font-mono text-3xl text-neon-cyan">⚡</div>
            <div className="font-mono text-xl text-gray-400">
              Ready to execute your SQL query
            </div>
            <div className="font-mono text-sm text-gray-500">
              Enter a query above and click "Execute Query" or "Preview Query" to see results
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { columns, rows, rowCount } = result;

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6">
      {/* Results Header */}
      <div className="neon-panel p-6">
        <div className="neon-panel-header">
          {isPreview ? 'Query Preview Results' : 'Query Results'}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 font-mono text-sm">
          <div className="text-center">
            <div className="text-gray-400 mb-2">Rows Returned:</div>
            <div className="text-neon-green text-lg">{rows.length}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 mb-2">Total Rows:</div>
            <div className="text-neon-blue text-lg">{rowCount}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 mb-2">Columns:</div>
            <div className="text-neon-yellow text-lg">{columns.length}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 mb-2">Query Type:</div>
            <div className={`text-lg ${isPreview ? 'text-neon-purple' : 'text-neon-cyan'}`}>
              {isPreview ? '[PREVIEW]' : '[FULL]'}
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="neon-panel">
        <div className="neon-panel-header">
          Data Table
        </div>
        <div className="p-6">
          {isPreview && (
            <div className="mb-4 neon-panel p-3 text-center">
              <div className="font-mono text-sm text-neon-purple">
                ⚠️ PREVIEW MODE: Showing limited results. Use "Execute Query" for full dataset.
              </div>
            </div>
          )}

          {rows.length === 0 ? (
            <div className="text-center py-12">
              <div className="font-mono text-xl text-gray-400 mb-4">
                No data returned
              </div>
              <div className="font-mono text-sm text-gray-500">
                Your query executed successfully but returned no rows.
              </div>
            </div>
          ) : (
            <div className="overflow-auto max-h-96 border border-neon-blue rounded">
              <table className="neon-table">
                <thead className="sticky top-0">
                  <tr>
                    {columns.map((column, index) => (
                      <th key={index} className="whitespace-nowrap">
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex} className="whitespace-nowrap">
                          {cell === null ? (
                            <span className="text-gray-500 italic">NULL</span>
                          ) : cell === undefined ? (
                            <span className="text-gray-500 italic">UNDEFINED</span>
                          ) : (
                            String(cell)
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Data Summary */}
          <div className="mt-6 neon-panel p-4">
            <div className="neon-panel-header">
              Data Summary
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-mono text-sm">
              <div className="text-center">
                <div className="text-gray-400 mb-2">Memory Usage:</div>
                <div className="text-neon-cyan">
                  ~{Math.round((rows.length * columns.length * 50) / 1024)} KB
                </div>
              </div>
              <div className="text-center">
                <div className="text-gray-400 mb-2">Data Density:</div>
                <div className="text-neon-green">
                  {rows.length > 0 ? Math.round((rows.flat().filter(cell => cell !== null && cell !== undefined).length / (rows.length * columns.length)) * 100) : 0}%
                </div>
              </div>
              <div className="text-center">
                <div className="text-gray-400 mb-2">Processing:</div>
                <div className="text-neon-yellow">LOCAL</div>
              </div>
            </div>
          </div>

          {/* Footer Info */}
          <div className="mt-4 font-mono text-xs text-gray-500 text-center space-y-1">
            <div>• All data processing happens locally in your browser</div>
            <div>• Scroll horizontally/vertically to view more data</div>
            <div>• Use export buttons above to download results</div>
            {isPreview && (
              <div className="text-neon-purple">• This is a preview - run full query to see all results</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultPreview; 